import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@os/components/ui/card';
import { Button } from '@os/components/ui/button';
import { Input } from '@os/components/ui/input';
import { Label } from '@os/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@os/components/ui/select';
import { Textarea } from '@os/components/ui/textarea';
import { Badge } from '@os/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@os/components/ui/table';
import { TrendingUp, AlertCircle, BarChart3 } from 'lucide-react';
import * as ss from 'simple-statistics';
// @ts-ignore
import jStat from 'jstat';

interface RegressionAnalysisProps {
  data: number[];
}

interface RegressionResult {
  regressionType: string;
  coefficients: number[];
  rSquared: number;
  adjustedRSquared?: number;
  standardError: number;
  fStatistic?: number;
  pValue?: number;
  residuals: number[];
  predictions: number[];
  equation: string;
  interpretation: string;
}

export const RegressionAnalysis: React.FC<RegressionAnalysisProps> = ({ data }) => {
  const [regressionType, setRegressionType] = useState('linear');
  const [dependentVariable, setDependentVariable] = useState('');
  const [independentVariables, setIndependentVariables] = useState('');
  const [polynomialDegree, setPolynomialDegree] = useState('2');
  const [result, setResult] = useState<RegressionResult | null>(null);

  const parseVariableData = (input: string): number[] => {
    try {
      const cleaned = input.replace(/[^\d\.\-\s,\n]/g, '');
      return cleaned
        .split(/[\s,\n]+/)
        .filter(v => v.trim() !== '')
        .map(v => parseFloat(v.trim()))
        .filter(v => !isNaN(v));
    } catch {
      return [];
    }
  };

  const performLinearRegression = () => {
    const yData = parseVariableData(dependentVariable);
    const xData = parseVariableData(independentVariables);
    
    if (xData.length === 0 || yData.length === 0 || xData.length !== yData.length) {
      alert('Please ensure both variables have the same number of valid data points.');
      return null;
    }

    // Prepare data points for simple-statistics
    const dataPoints = xData.map((x, i) => [x, yData[i]]);
    
    // Calculate linear regression
    const regressionLine = ss.linearRegression(dataPoints);
    const rSquared = ss.rSquared(dataPoints, (x: number) => regressionLine.m * x + regressionLine.b);
    
    // Calculate predictions and residuals
    const predictions = xData.map(x => regressionLine.m * x + regressionLine.b);
    const residuals = yData.map((y, i) => y - predictions[i]);
    
    // Calculate standard error
    const n = dataPoints.length;
    const sumSquaredResiduals = residuals.reduce((sum, r) => sum + r * r, 0);
    const standardError = Math.sqrt(sumSquaredResiduals / (n - 2));
    
    // F-statistic and p-value
    const meanY = ss.mean(yData);
    const totalSumSquares = yData.reduce((sum, y) => sum + Math.pow(y - meanY, 2), 0);
    const regressionSumSquares = totalSumSquares * rSquared;
    const fStatistic = (regressionSumSquares / 1) / (sumSquaredResiduals / (n - 2));
    const pValue = 1 - jStat.centralF.cdf(fStatistic, 1, n - 2);
    
    const equation = `y = ${regressionLine.m.toFixed(4)}x + ${regressionLine.b.toFixed(4)}`;
    const interpretation = `The linear model explains ${(rSquared * 100).toFixed(2)}% of the variance in the dependent variable. ${
      pValue < 0.05 ? 'The regression is statistically significant.' : 'The regression is not statistically significant.'
    }`;

    return {
      regressionType: 'Linear Regression',
      coefficients: [regressionLine.b, regressionLine.m],
      rSquared,
      standardError,
      fStatistic,
      pValue,
      residuals,
      predictions,
      equation,
      interpretation
    };
  };

  const performPolynomialRegression = () => {
    const yData = parseVariableData(dependentVariable);
    const xData = parseVariableData(independentVariables);
    const degree = parseInt(polynomialDegree);
    
    if (xData.length === 0 || yData.length === 0 || xData.length !== yData.length) {
      alert('Please ensure both variables have the same number of valid data points.');
      return null;
    }

    if (xData.length <= degree) {
      alert('Sample size must be greater than polynomial degree.');
      return null;
    }

    // Create design matrix for polynomial regression
    const n = xData.length;
    const X: number[][] = [];
    
    for (let i = 0; i < n; i++) {
      const row: number[] = [];
      for (let j = 0; j <= degree; j++) {
        row.push(Math.pow(xData[i], j));
      }
      X.push(row);
    }

    // Calculate coefficients using normal equation: β = (X'X)^(-1)X'y
    try {
      const XT = transpose(X);
      const XTX = matrixMultiply(XT, X);
      const XTXInv = matrixInverse(XTX);
      const XTy = matrixVectorMultiply(XT, yData);
      const coefficients = matrixVectorMultiply(XTXInv, XTy);

      // Calculate predictions
      const predictions = X.map(row => 
        row.reduce((sum, x, j) => sum + coefficients[j] * x, 0)
      );

      // Calculate residuals
      const residuals = yData.map((y, i) => y - predictions[i]);

      // Calculate R-squared
      const meanY = ss.mean(yData);
      const totalSumSquares = yData.reduce((sum, y) => sum + Math.pow(y - meanY, 2), 0);
      const residualSumSquares = residuals.reduce((sum, r) => sum + r * r, 0);
      const rSquared = 1 - (residualSumSquares / totalSumSquares);
      
      // Adjusted R-squared
      const adjustedRSquared = 1 - ((1 - rSquared) * (n - 1)) / (n - degree - 1);

      // Standard error
      const standardError = Math.sqrt(residualSumSquares / (n - degree - 1));

      // Create equation string
      const terms = coefficients.map((coef, i) => {
        if (i === 0) return coef.toFixed(4);
        if (i === 1) return `${coef >= 0 ? '+' : ''}${coef.toFixed(4)}x`;
        return `${coef >= 0 ? '+' : ''}${coef.toFixed(4)}x^${i}`;
      });
      const equation = `y = ${terms.join('')}`;

      const interpretation = `The polynomial model (degree ${degree}) explains ${(rSquared * 100).toFixed(2)}% of the variance. Adjusted R² = ${adjustedRSquared.toFixed(4)}.`;

      return {
        regressionType: `Polynomial Regression (Degree ${degree})`,
        coefficients,
        rSquared,
        adjustedRSquared,
        standardError,
        residuals,
        predictions,
        equation,
        interpretation
      };
    } catch (error) {
      alert('Error performing polynomial regression. The design matrix may be singular.');
      return null;
    }
  };

  // Matrix operations helpers
  const transpose = (matrix: number[][]): number[][] => {
    return matrix[0].map((_, colIndex) => matrix.map(row => row[colIndex]));
  };

  const matrixMultiply = (a: number[][], b: number[][]): number[][] => {
    const result: number[][] = [];
    for (let i = 0; i < a.length; i++) {
      result[i] = [];
      for (let j = 0; j < b[0].length; j++) {
        let sum = 0;
        for (let k = 0; k < b.length; k++) {
          sum += a[i][k] * b[k][j];
        }
        result[i][j] = sum;
      }
    }
    return result;
  };

  const matrixVectorMultiply = (matrix: number[][], vector: number[]): number[] => {
    return matrix.map(row => 
      row.reduce((sum, val, i) => sum + val * vector[i], 0)
    );
  };

  const matrixInverse = (matrix: number[][]): number[][] => {
    const n = matrix.length;
    const identity = Array(n).fill(0).map((_, i) => 
      Array(n).fill(0).map((_, j) => i === j ? 1 : 0)
    );
    
    // Create augmented matrix
    const augmented = matrix.map((row, i) => [...row, ...identity[i]]);
    
    // Gaussian elimination
    for (let i = 0; i < n; i++) {
      // Find pivot
      let maxRow = i;
      for (let k = i + 1; k < n; k++) {
        if (Math.abs(augmented[k][i]) > Math.abs(augmented[maxRow][i])) {
          maxRow = k;
        }
      }
      
      // Swap rows
      [augmented[i], augmented[maxRow]] = [augmented[maxRow], augmented[i]];
      
      // Make diagonal element 1
      const pivot = augmented[i][i];
      if (Math.abs(pivot) < 1e-10) throw new Error('Matrix is singular');
      
      for (let j = 0; j < 2 * n; j++) {
        augmented[i][j] /= pivot;
      }
      
      // Eliminate column
      for (let k = 0; k < n; k++) {
        if (k !== i) {
          const factor = augmented[k][i];
          for (let j = 0; j < 2 * n; j++) {
            augmented[k][j] -= factor * augmented[i][j];
          }
        }
      }
    }
    
    // Extract inverse matrix
    return augmented.map(row => row.slice(n));
  };

  const runRegression = () => {
    let regressionResult: RegressionResult | null = null;

    switch (regressionType) {
      case 'linear':
        regressionResult = performLinearRegression();
        break;
      case 'polynomial':
        regressionResult = performPolynomialRegression();
        break;
    }

    setResult(regressionResult);
  };

  const regressionOptions = [
    { value: 'linear', label: 'Linear Regression', description: 'Simple linear relationship (y = mx + b)' },
    { value: 'polynomial', label: 'Polynomial Regression', description: 'Polynomial relationship of specified degree' }
  ];

  if (data.length === 0) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <TrendingUp className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Data Available</h3>
          <p className="text-muted-foreground">Enter numerical data to begin regression analysis.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-orange-500 text-white rounded-lg">
          <TrendingUp size={24} />
        </div>
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Regression Analysis</h2>
          <p className="text-muted-foreground">Model relationships between variables</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configuration */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Regression Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="regression-type">Regression Type</Label>
                <Select value={regressionType} onValueChange={setRegressionType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {regressionOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div>
                          <div className="font-medium">{option.label}</div>
                          <div className="text-xs text-muted-foreground">{option.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="dependent-variable">Dependent Variable (Y)</Label>
                <Textarea
                  id="dependent-variable"
                  placeholder="Enter Y values (response variable)..."
                  value={dependentVariable}
                  onChange={(e) => setDependentVariable(e.target.value)}
                  className="min-h-[80px]"
                />
                {parseVariableData(dependentVariable).length > 0 && (
                  <div className="text-xs text-muted-foreground mt-1">
                    Parsed: {parseVariableData(dependentVariable).length} values
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="independent-variables">Independent Variable (X)</Label>
                <Textarea
                  id="independent-variables"
                  placeholder="Enter X values (predictor variable)..."
                  value={independentVariables}
                  onChange={(e) => setIndependentVariables(e.target.value)}
                  className="min-h-[80px]"
                />
                {parseVariableData(independentVariables).length > 0 && (
                  <div className="text-xs text-muted-foreground mt-1">
                    Parsed: {parseVariableData(independentVariables).length} values
                  </div>
                )}
              </div>

              {regressionType === 'polynomial' && (
                <div>
                  <Label htmlFor="polynomial-degree">Polynomial Degree</Label>
                  <Select value={polynomialDegree} onValueChange={setPolynomialDegree}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2">2 (Quadratic)</SelectItem>
                      <SelectItem value="3">3 (Cubic)</SelectItem>
                      <SelectItem value="4">4 (Quartic)</SelectItem>
                      <SelectItem value="5">5 (Quintic)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button onClick={runRegression} className="w-full">
                Run Regression Analysis
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Results */}
        <div className="lg:col-span-2">
          {result && (
            <div className="space-y-4">
              {/* Model Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{result.regressionType} Results</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <div className="text-sm text-muted-foreground">R²</div>
                      <div className="text-lg font-mono font-bold">{result.rSquared.toFixed(4)}</div>
                    </div>
                    {result.adjustedRSquared && (
                      <div>
                        <div className="text-sm text-muted-foreground">Adj. R²</div>
                        <div className="text-lg font-mono font-bold">{result.adjustedRSquared.toFixed(4)}</div>
                      </div>
                    )}
                    <div>
                      <div className="text-sm text-muted-foreground">Std. Error</div>
                      <div className="text-lg font-mono font-bold">{result.standardError.toFixed(4)}</div>
                    </div>
                    {result.pValue && (
                      <div>
                        <div className="text-sm text-muted-foreground">p-value</div>
                        <div className="text-lg font-mono font-bold">
                          {result.pValue < 0.001 ? '<0.001' : result.pValue.toFixed(4)}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div className="p-3 bg-muted rounded-lg">
                      <div className="text-sm font-medium mb-1">Regression Equation:</div>
                      <div className="font-mono text-sm">{result.equation}</div>
                    </div>

                    <div className="p-3 bg-muted rounded-lg">
                      <div className="text-sm font-medium mb-1">Interpretation:</div>
                      <div className="text-sm">{result.interpretation}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Coefficients Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Model Coefficients</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Term</TableHead>
                        <TableHead className="text-right">Coefficient</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.coefficients.map((coef, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {index === 0 ? 'Intercept' : 
                             index === 1 ? 'x' : 
                             `x^${index}`}
                          </TableCell>
                          <TableCell className="text-right font-mono">{coef.toFixed(6)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Residual Analysis */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Residual Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Mean Residual</div>
                      <div className="text-lg font-mono font-bold">
                        {ss.mean(result.residuals).toFixed(6)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Residual Std</div>
                      <div className="text-lg font-mono font-bold">
                        {ss.standardDeviation(result.residuals).toFixed(4)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Min Residual</div>
                      <div className="text-lg font-mono font-bold">
                        {Math.min(...result.residuals).toFixed(4)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Max Residual</div>
                      <div className="text-lg font-mono font-bold">
                        {Math.max(...result.residuals).toFixed(4)}
                      </div>
                    </div>
                  </div>

                  <div className="text-sm text-muted-foreground">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-4 w-4 text-amber-500" />
                      Model Assumptions to Check:
                    </div>
                    <ul className="space-y-1 ml-6">
                      <li>• Residuals should be normally distributed</li>
                      <li>• Residuals should have constant variance (homoscedasticity)</li>
                      <li>• Residuals should be independent</li>
                      <li>• Relationship should be linear (for linear regression)</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {!result && (
            <Card>
              <CardContent className="py-12 text-center">
                <TrendingUp className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">Configure Your Regression</h3>
                <p className="text-muted-foreground">
                  Enter your dependent and independent variables to begin regression analysis.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};